import { gql } from '../../types/__codegen__/sanity';

export const GET_TOC_PERKS = gql(`
  query getTocPerks($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on MemberPerk {
              ...PerkFields
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_PERKS_FOR_OFFER_PAGE = gql(`
  query getTocPerksForOfferPage($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on MemberPerk {
              slogan
              name
              imageObject {
                ...ImageObjectFields
              }
              release {
                ...ReleaseFields
              }
              urlObject {
                ...UrlObjectFields
              }
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_ANNOUNCEMENTS = gql(`
  query getTocAnnouncements($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on Alert {
              _id
              _type
              name
              title
              descriptionRaw
              level
            }
            ... on BrandCampaign {
              _id
              _type
              name
              sloganRaw
              descriptionRaw
              imageObject {
                ...ImageObjectFields
              }
              urlObject {
                ...UrlObjectFields
              }
            }
            ... on ComplianceIncident {
              _id
              _type
              name
              descriptionRaw
              imageObject {
                ...ImageObjectFields
              }
              urlObject {
                ...UrlObjectFields
              }
            }
            ... on ExternalContent {
              _id
              _type
              identifier {
                current
              }
              title
            }
            ... on Legislation {
              _id
              _type
              name
              descriptionRaw
              imageObject {
                ...ImageObjectFields
              }
              urlObject {
                ...UrlObjectFields
              }
            }
            ... on ProductListSection {
              title
              _id
              _type
              productLists {
                __typename
                ... on ProductList {
                  ...ProductListFields
                }
              }
            }
            ... on PromotionalCtaList {
              name
              _id
              _type
              items {
                ... on Service {
                  _id
                  ...ContentTileFields
                  imageObject {
                    ...ImageObjectFields
                  }
                }
              }
            }
            ... on QuestionsList {
              title
              questions {
                ... on Question {
                  _id
                  question
                  answerRaw
                }
              }
            }
            ... on SalesJourney {
              _id
              _type
              name
              descriptionRaw
              imageObject {
                ...ImageObjectFields
              }
              urlObject {
                ...UrlObjectFields
              }
            }
            ... on UnmodelledAnnouncement {
              _id
              _type
              name
              sloganRaw
              descriptionRaw
              imageObject {
                ...ImageObjectFields
              }
              urlObject {
                ...UrlObjectFields
              }
            }
            ... on ServiceChannel {
              _id
              _type
              name
              sloganRaw
              descriptionRaw
              imageObject {
                ...ImageObjectFields
              }
              installUrlObjects {
                name
                url
                image {
                  ...ImageFields
                }
              }
            }
            ... on ValuePropositionGroup {
              _id
              _type
              descriptionRaw
              title
              valuePropositions {
                sloganRaw: name
                summaryRaw
                urlObject {
                  ...UrlObjectFields
                }
                imageObject {
                  ...ImageObjectFields
                }
              }
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_AWARDS = gql(`
  query getTocAwards($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on Award {
              imageObject {
                ...ImageObjectFields
              }
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_WHY_AHM = gql(`
  query getTocWhyAhm($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on ValuePropositionGroup {
              _id
              _type
              description: descriptionRaw
              name: title
              valuePropositionsReference: valuePropositions {
                name
                slogan: sloganRaw
                summary: summaryRaw
                urlObject {
                  ...UrlObjectFields
                }
                imageObject {
                  ...ImageObjectFields
                }
              }
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_CONTACTS = gql(`
  query getTocContacts($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on Contact {
              action
              descriptionRaw
              name
              phoneNumber
              title
              type
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_FEATURED_ARTICLE = gql(`
  query getTocFeaturedArticle($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on Article {
              _createdAt
              bodyRaw
              name
              insuranceType {
                identifier {
                  ...SlugFields
                }
              }
              identifier {
                ...SlugFields
              }
              descriptionRaw
              tags {
                name
              }
              imageObject {
                ...ImageObjectFields
              }
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_ANNOUNCEMENT_BY_INSURANCE_TYPE = gql(`
  query getTocAnnouncementByInsuranceType($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      name
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on Alert {
              name
              descriptionRaw
              level
            }
          }
          release {
            ...ReleaseFields
          }
        }
      }
    }
  }
`);

export const GET_TOC_TERMS_AND_CONDITIONS = gql(`
  query getTocTermsAndConditions($identifier: String!) {
    allTableOfContentsList(where: { identifier: { current: { eq: $identifier } } }) {
      _id
      name
      description
      _createdAt
      items {
        ... on TableOfContentsItem {
          name
          item {
            ... on TermsAndConditions {
              _id
              descriptionRaw
              effectiveDate
            }
          }
        }
      }
    }
  }
`);
